import { configureStore } from "@reduxjs/toolkit";
// Or from '@reduxjs/toolkit/query/react'
import { setupListeners } from "@reduxjs/toolkit/query";
import { baseService } from "./base.store";
import { FunctionsError } from "firebase/functions";
import { toastSlice } from "./toast.store";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

export const store = configureStore({
  reducer: {
    [baseService.reducerPath]: baseService.reducer,
    toast: toastSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(baseService.middleware),
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export type GenericQueryReturnType<T extends string, D> =
  | {
      data: {
        [key in T]: D;
      };
    }
  | { error: boolean };

export type GenericUpdateReturnType =
  | {
      data: void;
    }
  | { error: string };

export type GenericCreateReturnType<T extends string, D> =
  | {
      data: {
        [key in T]: D;
      };
    }
  | { error: string };

export type GenericReturnType<D> =
  | {
      data: D;
    }
  | { error: string };

export const handleFirebaseError = (error: unknown, fallbackMessage = "Something went wrong"): { error: string } => {
  if (typeof error === "object" && error !== null) {
    const err = error as Partial<FunctionsError>;

    const message = err.message ?? fallbackMessage;

    return { error: message };
  }

  return { error: fallbackMessage };
};

export const executeQuery = async <Result, P extends any[]>(
  fn: (...params: P) => Promise<Result | null>,
  ...params: P
): Promise<GenericReturnType<Result>> => {
  try {
    const data = await fn(...params);
    if (data === null)
      return {
        error: "Something went wrong",
      };
    return { data };
  } catch (error) {
    console.log(error);
    return {
      error: "Something went wrong",
    };
  }
};

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);
